/**
 * ugc-media-widget.js
 * UGC Media Widget — Dawn OS 2.0
 *
 * Responsibilities:
 *  - CSS/JS asset injection (self-contained)
 *  - Horizontal scroll track management
 *  - Auto-scroll loop with pause-on-hover
 *  - Arrow visibility + disabled state
 *  - Dot indicator sync
 *  - Lightbox open/close/navigate
 *  - Inline video play/pause on item click
 *  - Intersection Observer for lazy inline video play
 *  - Keyboard navigation (arrows, Escape)
 *  - Focus trap inside lightbox
 *  - Reduced motion respect
 */

(function () {
  'use strict';

  /* ── Inject CSS asset once ───────────────────────────────── */
  if (!document.getElementById('ugc-media-widget-css')) {
    const link = document.createElement('link');
    link.id = 'ugc-media-widget-css';
    link.rel = 'stylesheet';
    link.href = window.Shopify
      ? window.Shopify.routes
        ? window.Shopify.routes.root + 'cdn/shop/files/ugc-media-widget.css'
        : '/assets/ugc-media-widget.css'
      : '/assets/ugc-media-widget.css';
    // Reliable Dawn asset path
    link.href = document.querySelector('link[href*="ugc-media-widget.css"]')
      ? document.querySelector('link[href*="ugc-media-widget.css"]').href
      : '/assets/ugc-media-widget.css';
    document.head.appendChild(link);
  }

  /* ── Helpers ─────────────────────────────────────────────── */

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.from((context || document).querySelectorAll(selector));
  }

  /* ── UGCWidget class ─────────────────────────────────────── */

  class UGCWidget {
    constructor(el) {
      this.section       = el;
      this.track         = qs('[data-ugc-track]', el);
      this.slides        = qsa('[data-ugc-item]', el);
      this.prevBtn       = qs('[data-ugc-prev]', el);
      this.nextBtn       = qs('[data-ugc-next]', el);
      this.dots          = qsa('[data-ugc-dot]', el);
      this.trackWrap     = qs('[data-ugc-track-wrap]', el);

      if (!this.track) return;

      this.count         = this.slides.length;
      this.autoScroll    = this.track.dataset.autoScroll === 'true';
      this.scrollSpeed   = parseInt(this.track.dataset.scrollSpeed, 10) || 4;
      this.interaction   = this.track.dataset.interaction || 'lightbox';
      this.autoTimer     = null;
      this.isHovered     = false;
      this.currentIndex  = 0;

      this._init();
    }

    _init() {
      this._bindArrows();
      this._bindDots();
      this._bindItems();
      this._bindTrackScroll();

      if (this.count > 4) {
        this._showArrows();
        if (this.autoScroll && !prefersReducedMotion) {
          this._startAutoScroll();
          this._bindHoverPause();
        }
      }
    }

    /* ── Arrow buttons ──────────────────────────────────────── */

    _showArrows() {
      if (this.prevBtn) {
        this.prevBtn.classList.add('is-visible');
        this.nextBtn.classList.add('is-visible');
      }
      this._updateArrowState();
    }

    _updateArrowState() {
      if (!this.prevBtn) return;
      const t = this.track;
      const atStart = t.scrollLeft <= 4;
      const atEnd   = t.scrollLeft + t.clientWidth >= t.scrollWidth - 4;
      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    }

    _bindArrows() {
      if (!this.prevBtn) return;
      this.prevBtn.addEventListener('click', () => this._scrollBy(-1));
      this.nextBtn.addEventListener('click', () => this._scrollBy(1));
    }

    _scrollBy(direction) {
      const itemWidth = this._getItemWidth();
      this.track.scrollBy({ left: direction * itemWidth, behavior: 'smooth' });
    }

    _getItemWidth() {
      const firstSlide = this.track.querySelector('.ugc-widget__slide');
      if (!firstSlide) return 280;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 12;
      return firstSlide.offsetWidth + gap;
    }

    /* ── Track scroll → sync dots + arrows ─────────────────── */

    _bindTrackScroll() {
      let rafId;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          this._updateArrowState();
          this._syncDots();
        });
      }, { passive: true });
    }

    /* ── Dots ───────────────────────────────────────────────── */

    _bindDots() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.ugcDot, 10);
          this._scrollToIndex(idx);
        });
      });
    }

    _syncDots() {
      const itemWidth = this._getItemWidth();
      const nearest   = Math.round(this.track.scrollLeft / itemWidth);
      this.currentIndex = Math.min(nearest, this.count - 1);
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentIndex);
      });
    }

    _scrollToIndex(idx) {
      const itemWidth = this._getItemWidth();
      this.track.scrollTo({ left: idx * itemWidth, behavior: 'smooth' });
    }

    /* ── Auto-scroll ────────────────────────────────────────── */

    _startAutoScroll() {
      // speed 1–10 → interval 4000–800ms
      const ms = Math.round(4000 - (this.scrollSpeed - 1) * (3200 / 9));
      this.autoTimer = setInterval(() => {
        if (this.isHovered) return;
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 4;
        if (atEnd) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this._scrollBy(1);
        }
      }, ms);
    }

    _stopAutoScroll() {
      clearInterval(this.autoTimer);
    }

    _bindHoverPause() {
      this.section.addEventListener('mouseenter', () => { this.isHovered = true; });
      this.section.addEventListener('mouseleave', () => { this.isHovered = false; });
      this.section.addEventListener('focusin',    () => { this.isHovered = true; });
      this.section.addEventListener('focusout',   () => { this.isHovered = false; });
    }

    /* ── Media item interaction ─────────────────────────────── */

    _bindItems() {
      this.slides.forEach((item) => {
        item.addEventListener('click', () => this._handleItemClick(item));
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this._handleItemClick(item);
          }
        });

        // Inline video: IntersectionObserver to autoplay on entry
        if (this.interaction === 'inline') {
          const vid = qs('[data-ugc-inline-video]', item);
          if (vid) {
            // Direct click on video element → open lightbox (belt-and-suspenders
            // alongside pointer-events:none in CSS)
            vid.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              UGCLightbox.open(this._buildMediaList(), parseInt(item.dataset.index, 10));
            });
            this._observeInlineVideo(item, vid);
          }
        }
      });
    }

    _handleItemClick(item) {
      const type = item.dataset.type;
      const idx  = parseInt(item.dataset.index, 10);

      // Always open lightbox on click — for both images and videos.
      // In inline mode, videos autoplay muted via IntersectionObserver (scroll).
      // Clicking any item opens lightbox: images for zoom, videos for full audio.
      UGCLightbox.open(this._buildMediaList(), idx);
    }

    _toggleInlineVideo(item) {
      const vid = qs('[data-ugc-inline-video]', item);
      if (!vid) return;
      if (vid.paused) {
        vid.muted = true;
        vid.play().catch(() => {});
        item.classList.add('is-playing');
      } else {
        vid.pause();
        item.classList.remove('is-playing');
      }
    }

    _observeInlineVideo(item, vid) {
      if (!('IntersectionObserver' in window)) return;
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            vid.muted = true;
            vid.play().catch(() => {});
            item.classList.add('is-playing');
          } else {
            vid.pause();
            item.classList.remove('is-playing');
          }
        });
      }, { threshold: 0.5 });
      obs.observe(item);
    }

    _buildMediaList() {
      return this.slides.map((item) => ({
        type:   item.dataset.type,
        src:    item.dataset.src,
        poster: item.dataset.poster || null,
        index:  parseInt(item.dataset.index, 10),
      }));
    }
  }

  /* ── UGCLightbox singleton ───────────────────────────────── */

  const UGCLightbox = {
    el:          null,
    mediaWrap:   null,
    counter:     null,
    prevBtn:     null,
    nextBtn:     null,
    items:       [],
    currentIdx:  0,
    focusableEls: [],
    _prevFocused: null,

    _ensureDOM() {
      if (this.el) return;
      this.el        = qs('#ugc-lightbox');
      if (!this.el) return;
      // Move lightbox to <body> so position:fixed escapes any section
      // stacking/overflow context (Dawn wraps sections in transformed/clipped divs).
      if (this.el.parentElement !== document.body) {
        document.body.appendChild(this.el);
      }
      this.mediaWrap = qs('#ugc-lightbox-media', this.el);
      this.counter   = qs('#ugc-lightbox-counter', this.el);
      this.prevBtn   = qs('[data-ugc-lightbox-prev]', this.el);
      this.nextBtn   = qs('[data-ugc-lightbox-next]', this.el);
      this._bindEvents();
    },

    _bindEvents() {
      qsa('[data-ugc-lightbox-close]', this.el).forEach((btn) => {
        btn.addEventListener('click', () => this.close());
      });
      this.prevBtn.addEventListener('click', () => this.navigate(-1));
      this.nextBtn.addEventListener('click', () => this.navigate(1));

      document.addEventListener('keydown', (e) => {
        if (!this.el || this.el.hidden) return;
        if (e.key === 'Escape')     this.close();
        if (e.key === 'ArrowLeft')  this.navigate(-1);
        if (e.key === 'ArrowRight') this.navigate(1);
        if (e.key === 'Tab')        this._trapFocus(e);
      });
    },

    open(items, idx) {
      this._ensureDOM();
      if (!this.el) return;

      this.items      = items;
      this.currentIdx = idx;

      this._render();
      this.el.hidden  = false;
      document.body.style.overflow = 'hidden';

      this._prevFocused = document.activeElement;
      this.el.querySelector('.ugc-lightbox__close')?.focus();
    },

    close() {
      if (!this.el) return;
      this._clearMedia();
      this.el.hidden = true;
      document.body.style.overflow = '';
      this._prevFocused?.focus();
    },

    navigate(dir) {
      const next = this.currentIdx + dir;
      if (next < 0 || next >= this.items.length) return;
      this.currentIdx = next;
      this._render();
    },

    _render() {
      this._clearMedia();
      const item = this.items[this.currentIdx];
      if (!item) return;

      let node;
      if (item.type === 'video') {
        node = document.createElement('video');
        node.src         = item.src;
        node.poster      = item.poster || '';
        node.controls    = true;
        node.autoplay    = true;
        node.muted       = false;
        node.playsinline = true;
        node.style.maxHeight = 'calc(100vh - 8rem)';
      } else {
        node = document.createElement('img');
        node.src = item.src;
        node.alt = '';
      }

      this.mediaWrap.appendChild(node);

      // Counter
      if (this.items.length > 1) {
        this.counter.textContent = `${this.currentIdx + 1} / ${this.items.length}`;
      }

      // Nav arrows
      const showNav = this.items.length > 1;
      this.prevBtn.hidden = !showNav || this.currentIdx === 0;
      this.nextBtn.hidden = !showNav || this.currentIdx === this.items.length - 1;
    },

    _clearMedia() {
      const vid = this.mediaWrap?.querySelector('video');
      if (vid) { vid.pause(); vid.src = ''; }
      if (this.mediaWrap) this.mediaWrap.innerHTML = '';
      if (this.counter) this.counter.textContent = '';
    },

    _trapFocus(e) {
      const focusable = qsa(
        'button:not([hidden]), [href], input, [tabindex]:not([tabindex="-1"])',
        this.el
      ).filter((el) => !el.closest('[hidden]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    },
  };

  /* ── Init on DOM ready + Shopify section events ──────────── */

  function initAll() {
    qsa('[data-ugc-section]').forEach((el) => {
      if (!el._ugcWidget) {
        el._ugcWidget = new UGCWidget(el);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);

  // Dawn section:load event (Theme Editor live-reload)
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    if (section.querySelector('[data-ugc-track]')) {
      section._ugcWidget = new UGCWidget(section);
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const widget = e.target._ugcWidget;
    if (widget) {
      widget._stopAutoScroll();
      e.target._ugcWidget = null;
    }
  });

})();